import { Loader2 } from 'lucide-react'
import { resolveValue, useToaster } from 'react-hot-toast'
import type { DefaultToastOptions, Toast } from 'react-hot-toast'

import { Alert } from '../ui/Alert'
import type { AlertSurface, AlertTone } from '../ui/Alert'

/**
 * LA PILE DE NOTIFICATIONS ÉPHÉMÈRES (LOT-H9).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI ON REND LES TOASTS SOI-MÊME
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `<Toaster />` de `react-hot-toast` est, par construction, un conteneur
 * `position: fixed`. C'est précisément ce que DA-002 interdit : « jamais en
 * `position: fixed` au-dessus d'une zone d'action ». Le lot H0 avait déjà
 * déplacé la pile SOUS l'en-tête (`--toast-stack-top`) ; la recette a mesuré
 * que ça ne suffisait pas — la première chose sous l'en-tête, c'est le cluster
 * d'actions du `PageHeader`, et les toasts se posaient dessus.
 *
 * L'API sans rendu (`useToaster`) donne les mêmes toasts, la même file, les
 * mêmes minuteries — mais nous laisse choisir OÙ ils atterrissent. Ils
 * atterrissent dans la zone de notification, dans le flux (voir
 * `NotificationRegion`).
 *
 * L'API d'appel ne bouge pas : les ~40 fichiers qui écrivent `toast.success(…)`
 * n'ont rien à changer.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CE QUI EST DÉLIBÉRÉMENT IGNORÉ DE L'APPELANT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'`icon` passée à `toast()` n'est PAS rendue : plusieurs appels y mettent un
 * emoji (`'📞'`), ce que DA-038 proscrit — le produit a un jeu d'icônes
 * vectorielles. L'icône vient du ton, comme dans tout `Alert`. Le `style` en
 * ligne est ignoré pour la même raison : il portait les six habillages que ce
 * lot supprime.
 */

/** Durées : inchangées par rapport à l'ancien `ToastProvider`. */
export const TOAST_OPTIONS: DefaultToastOptions = {
  duration: 2500,
  error: { duration: 3500 },
}

/**
 * Marqueurs de ton pour les toasts qui n'ont pas de type natif.
 * `react-hot-toast` ne connaît que `success` / `error` / `loading` / `blank` :
 * « avertissement » et « information » passent par `className`, seul champ
 * libre de l'API — et le seul qui traverse sans casser les appelants.
 */
export const TONE_CLASS: Record<'warning' | 'info', string> = {
  warning: 'toast-tone-warning',
  info: 'toast-tone-info',
}

export function toneOfToast(toast: Toast): AlertTone {
  const className = toast.className ?? ''
  if (className.includes(TONE_CLASS.warning)) return 'warning'
  if (className.includes(TONE_CLASS.info)) return 'info'

  switch (toast.type) {
    case 'success':
      return 'success'
    case 'error':
      return 'danger'
    default:
      return 'info'
  }
}

interface ToastListProps {
  toasts: Toast[]
  surface: AlertSurface
}

export function ToastList({ toasts, surface }: ToastListProps) {
  return (
    <>
      {toasts.map((toast) => (
        <Alert
          key={toast.id}
          tone={toneOfToast(toast)}
          surface={surface}
          role={toast.ariaProps.role}
          // Un toast `loading` n'a pas de ton : il a un état. Le spinner le dit,
          // et il remplace l'icône du ton — c'est le seul cas d'`icon` custom.
          icon={toast.type === 'loading' ? Loader2 : undefined}
          iconClassName={toast.type === 'loading' ? 'animate-spin' : ''}
          title={resolveValue(toast.message, toast)}
        />
      ))}
    </>
  )
}

/**
 * Les toasts VISIBLES. `react-hot-toast` garde un toast dans la file après sa
 * fermeture (le temps de l'animation de sortie) : sans ce filtre, la zone
 * resterait ouverte une seconde de plus à chaque message.
 */
export function useVisibleToasts(): Toast[] {
  const { toasts } = useToaster(TOAST_OPTIONS)
  return toasts.filter((toast) => toast.visible)
}
